import type {
  AdminExerciseCreateRequest,
  AdminExerciseDetail,
  AdminExerciseUpdateRequest,
  BodyPositionName,
  EquipmentName,
  ExerciseCategoryName,
  ExerciseDifficultyName,
  ImpactName,
  LateralityName,
  MechanicName,
  MovementPatternName,
  MuscleGroupName,
} from '@steado/admin-contract/exercises';
import type { AdminExerciseDraftExercise } from '@steado/admin-contract/exercise-requests';
import type { InjuryAreaName } from '@steado/admin-contract/user-detail';

/**
 * The shape the exercise form edits, shared by creating one and editing one.
 *
 * Framework-free so the rules can be asserted without rendering: what counts as
 * a change, what counts as incomplete, and how a block of typed text becomes
 * the app's numbered cue list.
 *
 * `slug` is deliberately absent. It is derived from the name at creation and
 * frozen after — plan generation is constrained to `availableExerciseSlugs` and
 * a stored plan holds the value it was generated against, so a slug that
 * followed a rename would stop matching rows that already exist.
 */
export interface ExerciseFormValues {
  name: string;
  category: ExerciseCategoryName;
  difficulty: ExerciseDifficultyName;
  primaryMuscles: MuscleGroupName[];
  secondaryMuscles: MuscleGroupName[];
  equipment: EquipmentName[];
  contraindications: InjuryAreaName[];
  /**
   * One cue per line, held as typed rather than as an array: splitting on every
   * keystroke would fight the operator over a trailing newline. Split on save.
   */
  howToSteps: string;
  isWeighted: boolean;
  /**
   * How the movement behaves. Null throughout, and null is a value: "nobody
   * has assessed this". The import leaves most of these empty on purpose, so a
   * form that could only ever hold an answer would make the review queue look
   * finished when it is not.
   */
  mechanic: MechanicName | null;
  movementPattern: MovementPatternName | null;
  laterality: LateralityName | null;
  impact: ImpactName | null;
  axialLoad: boolean | null;
  /**
   * What a pregnancy restriction reads, beside impact and difficulty. Null
   * for the same reason as the five above, and it matters more here: a `no`
   * nobody chose would clear a movement for a pregnant user on nobody's word.
   */
  bodyPosition: BodyPositionName | null;
  trunkFlexion: boolean | null;
  avoidInPregnancy: boolean | null;
  /**
   * Whether the movement needs a second person. `yes` keeps it out of every
   * generated plan and every suggestion; null reads as solo-capable, so
   * leaving it unset is the right answer for anything a person can do alone.
   */
  requiresPartner: boolean | null;
  /** Held as text so an emptied field is not silently a zero. */
  sortOrder: string;
}

export const EMPTY_EXERCISE_FORM: ExerciseFormValues = {
  name: '',
  category: 'push',
  // Intermediate, matching the contract's default: guessing low would offer the
  // movement to the users least able to judge whether they should attempt it.
  difficulty: 'intermediate',
  primaryMuscles: [],
  secondaryMuscles: [],
  equipment: [],
  contraindications: [],
  howToSteps: '',
  isWeighted: true,
  mechanic: null,
  movementPattern: null,
  laterality: null,
  impact: null,
  axialLoad: null,
  bodyPosition: null,
  trunkFlexion: null,
  avoidInPregnancy: null,
  requiresPartner: null,
  sortOrder: '0',
};

export function formValuesFrom(exercise: AdminExerciseDetail): ExerciseFormValues {
  return {
    name: exercise.name,
    category: exercise.category,
    difficulty: exercise.difficulty,
    primaryMuscles: [...exercise.primaryMuscles],
    secondaryMuscles: [...exercise.secondaryMuscles],
    equipment: [...exercise.equipment],
    contraindications: [...exercise.contraindications],
    howToSteps: exercise.howToSteps.join('\n'),
    isWeighted: exercise.isWeighted,
    mechanic: exercise.mechanic,
    movementPattern: exercise.movementPattern,
    laterality: exercise.laterality,
    impact: exercise.impact,
    axialLoad: exercise.axialLoad,
    bodyPosition: exercise.bodyPosition,
    trunkFlexion: exercise.trunkFlexion,
    avoidInPregnancy: exercise.avoidInPregnancy,
    requiresPartner: exercise.requiresPartner,
    sortOrder: String(exercise.sortOrder),
  };
}

/**
 * The form an exercise request's AI draft opens, every field the draft left
 * out at the empty form's value. The draft arrives already checked against
 * the create schema, so nothing here second-guesses a value; a missing name
 * falls back to the request's.
 */
export function formValuesFromDraft(
  draft: AdminExerciseDraftExercise | null,
  fallbackName: string,
): ExerciseFormValues {
  const values: ExerciseFormValues = { ...EMPTY_EXERCISE_FORM, name: fallbackName };
  if (draft === null) return values;

  return {
    name: draft.name ?? fallbackName,
    category: draft.category ?? values.category,
    difficulty: draft.difficulty ?? values.difficulty,
    primaryMuscles: [...(draft.primaryMuscles ?? [])],
    secondaryMuscles: [...(draft.secondaryMuscles ?? [])],
    equipment: [...(draft.equipment ?? [])],
    contraindications: [...(draft.contraindications ?? [])],
    howToSteps: (draft.howToSteps ?? []).join('\n'),
    isWeighted: draft.isWeighted ?? values.isWeighted,
    mechanic: draft.mechanic ?? null,
    movementPattern: draft.movementPattern ?? null,
    laterality: draft.laterality ?? null,
    impact: draft.impact ?? null,
    axialLoad: draft.axialLoad ?? null,
    bodyPosition: draft.bodyPosition ?? null,
    trunkFlexion: draft.trunkFlexion ?? null,
    avoidInPregnancy: draft.avoidInPregnancy ?? null,
    requiresPartner: draft.requiresPartner ?? null,
    sortOrder: draft.sortOrder === undefined ? values.sortOrder : String(draft.sortOrder),
  };
}

/** Blank lines dropped: they are how someone spaces text out, not a cue. */
export function parseSteps(text: string): string[] {
  return text
    .split('\n')
    .map((step) => step.trim())
    .filter((step) => step !== '');
}

function parseSortOrder(value: string): number {
  const parsed = Number.parseInt(value.trim(), 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
}

/**
 * Why the form cannot be saved yet, in the operator's words.
 *
 * These mirror the contract's own minimums rather than adding a second set of
 * rules, so the button is shut for the same reasons the server would refuse.
 */
export function formIssues(values: ExerciseFormValues): string[] {
  const issues: string[] = [];

  if (values.name.trim().length < 2) issues.push('Name it.');
  // An exercise that trains nothing cannot be selected by plan generation, so
  // the row would be inert rather than merely incomplete.
  if (values.primaryMuscles.length === 0) issues.push('Pick at least one primary muscle.');
  if (values.equipment.length === 0) issues.push('Pick at least one piece of equipment (or “none”).');
  if (values.howToSteps.split('\n').some((step) => step.trim().length > 280)) {
    issues.push('One of the cues is longer than 280 characters.');
  }

  return issues;
}

export function toCreateRequest(values: ExerciseFormValues): AdminExerciseCreateRequest {
  return {
    name: values.name.trim(),
    category: values.category,
    difficulty: values.difficulty,
    primaryMuscles: values.primaryMuscles,
    secondaryMuscles: values.secondaryMuscles,
    equipment: values.equipment,
    contraindications: values.contraindications,
    howToSteps: parseSteps(values.howToSteps),
    isWeighted: values.isWeighted,
    mechanic: values.mechanic,
    movementPattern: values.movementPattern,
    laterality: values.laterality,
    impact: values.impact,
    axialLoad: values.axialLoad,
    bodyPosition: values.bodyPosition,
    trunkFlexion: values.trunkFlexion,
    avoidInPregnancy: values.avoidInPregnancy,
    requiresPartner: values.requiresPartner,
    sortOrder: parseSortOrder(values.sortOrder),
  };
}

/**
 * Only what moved.
 *
 * The server drops unchanged fields anyway, so this is not what makes the audit
 * row honest — it is what stops the console claiming an edit it did not make
 * when two operators have the same row open.
 */
export function changedFields(
  original: ExerciseFormValues,
  current: ExerciseFormValues,
): AdminExerciseUpdateRequest {
  const patch: AdminExerciseUpdateRequest = {};

  if (current.name.trim() !== original.name.trim()) patch.name = current.name.trim();
  if (current.category !== original.category) patch.category = current.category;
  if (current.difficulty !== original.difficulty) patch.difficulty = current.difficulty;
  if (!sameList(current.primaryMuscles, original.primaryMuscles)) {
    patch.primaryMuscles = current.primaryMuscles;
  }
  if (!sameList(current.secondaryMuscles, original.secondaryMuscles)) {
    patch.secondaryMuscles = current.secondaryMuscles;
  }
  if (!sameList(current.equipment, original.equipment)) patch.equipment = current.equipment;
  if (!sameList(current.contraindications, original.contraindications)) {
    patch.contraindications = current.contraindications;
  }
  // Compared after parsing, so adding a blank line is not an edit — but a
  // reorder is, because the app numbers the cues.
  if (!sameList(parseSteps(current.howToSteps), parseSteps(original.howToSteps))) {
    patch.howToSteps = parseSteps(current.howToSteps);
  }
  if (current.isWeighted !== original.isWeighted) patch.isWeighted = current.isWeighted;
  // Compared with `!==`, so clearing one back to null is a change the patch
  // carries — the whole point of these being nullable is that a curator can
  // withdraw a wrong guess, not only replace it.
  if (current.mechanic !== original.mechanic) patch.mechanic = current.mechanic;
  if (current.movementPattern !== original.movementPattern) {
    patch.movementPattern = current.movementPattern;
  }
  if (current.laterality !== original.laterality) patch.laterality = current.laterality;
  if (current.impact !== original.impact) patch.impact = current.impact;
  if (current.axialLoad !== original.axialLoad) patch.axialLoad = current.axialLoad;
  if (current.bodyPosition !== original.bodyPosition) patch.bodyPosition = current.bodyPosition;
  if (current.trunkFlexion !== original.trunkFlexion) patch.trunkFlexion = current.trunkFlexion;
  if (current.avoidInPregnancy !== original.avoidInPregnancy) {
    patch.avoidInPregnancy = current.avoidInPregnancy;
  }
  if (current.requiresPartner !== original.requiresPartner) {
    patch.requiresPartner = current.requiresPartner;
  }
  if (parseSortOrder(current.sortOrder) !== parseSortOrder(original.sortOrder)) {
    patch.sortOrder = parseSortOrder(current.sortOrder);
  }

  return patch;
}

export function countChanges(original: ExerciseFormValues, current: ExerciseFormValues): number {
  return Object.keys(changedFields(original, current)).length;
}

/** Order matters: reordering the cues, or the muscles, is a real edit. */
function sameList<T>(a: readonly T[], b: readonly T[]): boolean {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

export function toggle<T extends string>(current: T[], value: T): T[] {
  return current.includes(value) ? current.filter((item) => item !== value) : [...current, value];
}
